import { useCallback, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { useOrderStore } from '../stores/useOrderStore';
import { useUIStore } from '../stores/useUIStore';
import { useAnalysisStore } from '../stores/useAnalysisStore';
import { parseRawOrders, runFullAnalysis } from '../lib/local-analysis';

export default function UploadPage() {
  const navigate = useNavigate();
  const { rawHeaders, rawPreview, totalRows, validRows, errorRows, fileName, setRawData, setFileName, setParsedOrders } =
    useOrderStore();
  const { setAnalysisComplete } = useUIStore();
  const { setMacro, setMicro, setStationAlerts } = useAnalysisStore();
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStep, setAnalysisStep] = useState('');
  const allDataRef = useRef<Record<string, unknown>[]>([]);
  const fileRef = useRef<File | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      fileRef.current = file;
      setFileName(file.name);
      setIsUploading(true);

      try {
        const buffer = await file.arrayBuffer();
        const worker = new Worker(
          new URL('../workers/excel-parser.worker.ts', import.meta.url),
          { type: 'module' }
        );

        await new Promise<void>((resolve, reject) => {
          worker.onmessage = (e) => {
            const { type, payload } = e.data;
            if (type === 'parsed') {
              allDataRef.current = payload.data;
              setRawData(payload.headers, payload.preview, payload.totalRows);
              worker.terminate();
              resolve();
            } else if (type === 'error') {
              worker.terminate();
              reject(new Error(payload.message));
            }
          };
          worker.onerror = (err) => {
            worker.terminate();
            reject(err);
          };
          worker.postMessage({
            type: 'parse',
            payload: { buffer, filename: file.name, mimeType: file.type },
          });
        });
      } catch (err) {
        console.error('Excel parse failed:', err);
        alert('文件解析失败，请检查文件格式');
      } finally {
        setIsUploading(false);
      }
    },
    [setRawData, setFileName],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleStartAnalysis = async () => {
    if (allDataRef.current.length === 0) return;
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    setAnalysisStep('');
    try {
      // Phase 1: Run analysis locally using the client-side engine
      setAnalysisStep('解析地址...');
      setAnalysisProgress(20);

      const parsedOrders = parseRawOrders(rawHeaders, allDataRef.current);
      if (parsedOrders.length === 0) {
        throw new Error('无法识别必要的列（收货地址等），请检查文件格式');
      }

      setAnalysisStep('四维评分计算...');
      setAnalysisProgress(50);

      const result = runFullAnalysis(parsedOrders);

      setAnalysisStep('生成报告...');
      setAnalysisProgress(80);

      setMacro(result.macro);
      setMicro(result.micro);
      setStationAlerts(result.stationAlerts);
      setParsedOrders(parsedOrders);

      setAnalysisProgress(100);
      setAnalysisStep('分析完成');
      setAnalysisComplete(true);
      navigate('/routing');
    } catch (err) {
      console.error('Analysis failed:', err);
      const msg = err instanceof Error ? err.message : String(err);
      alert(`分析失败: ${msg}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          欢迎使用 AI 发货策略中枢
        </h1>
        <p className="text-gray-500">
          上传您的订单数据，3分钟获得发货策略报告
        </p>
      </div>

      {/* Drop zone */}
      <div
        {...getRootProps()}
        className={[
          'border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors',
          isDragActive
            ? 'border-brand bg-blue-50'
            : 'border-gray-300 hover:border-brand hover:bg-gray-50',
        ].join(' ')}
      >
        <input {...getInputProps()} />
        <div className="space-y-3">
          <div className="text-4xl">📂</div>
          {isUploading ? (
            <p className="text-brand font-medium">正在解析文件...</p>
          ) : isDragActive ? (
            <p className="text-brand font-medium">释放文件以上传</p>
          ) : (
            <>
              <p className="text-gray-600 font-medium">
                拖拽 Excel 文件到此处，或点击选择文件
              </p>
              <p className="text-sm text-gray-400">
                支持 .xlsx / .xls / .csv，单次最大 10,000 条订单
              </p>
            </>
          )}
          {fileName && !isUploading && (
            <p className="text-sm text-brand font-medium">已选择: {fileName}</p>
          )}
        </div>
      </div>

      {/* Template download */}
      <div className="text-center">
        <button className="text-sm text-brand hover:underline">
          下载模板文件
        </button>
      </div>

      {/* Data preview */}
      {rawPreview.length > 0 && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-3">
              数据预览（前 {Math.min(rawPreview.length, 10)} 行）
            </h2>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {rawHeaders.map((h) => (
                      <th
                        key={h}
                        className="px-4 py-2.5 text-left font-medium text-gray-600 whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rawPreview.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      {rawHeaders.map((h) => (
                        <td
                          key={h}
                          className="px-4 py-2 text-gray-700 whitespace-nowrap"
                        >
                          {String(row[h] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Validation summary */}
          <div className="flex gap-4">
            <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-semibold text-gray-900">
                {totalRows.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500 mt-1">总订单</div>
            </div>
            <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-semibold text-success">
                {validRows > 0 ? validRows.toLocaleString() : totalRows.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500 mt-1">有效行</div>
            </div>
            <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4 text-center">
              <div className="text-2xl font-semibold text-danger">
                {errorRows}
              </div>
              <div className="text-sm text-gray-500 mt-1">异常行</div>
            </div>
          </div>

          {/* Start analysis button + progress */}
          <div className="text-center space-y-3">
            {isAnalyzing && (
              <div className="max-w-md mx-auto space-y-2">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand rounded-full transition-all duration-500"
                    style={{ width: `${analysisProgress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-500">
                  {analysisStep || '准备中...'} ({analysisProgress}%)
                </p>
              </div>
            )}
            <button
              onClick={handleStartAnalysis}
              disabled={isUploading || isAnalyzing}
              className="inline-flex items-center gap-2 px-8 py-3 bg-brand text-white font-medium rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {isAnalyzing ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  分析中...
                </>
              ) : (
                <>
                  开始分析
                  <span>→</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
